import { Action } from "redux";

import { EnumDescription, IEnumType } from "@multiweb/types/multiweb";
import { EnumTypeWithKey, ILoadEnumsAction, IReduxEnumDescription, IReduxEnumType } from "./types";

/**
 * Udělá konverzi mezi typem enumu ze staré části aplikace do typu enumu uchovávaného v reduxu.
 * @param enumType zkonvertuje se na {@link IReduxEnumType}
 */
export const convertToReduxEnumType = (enumType: IEnumType): IReduxEnumType => {
    return {
        descriptions: convertToReduxDescriptions(enumType.descriptions),
        type: enumType.type,
    };
};

/**
 * Udělá konverzi mezi popiskama ze staré části aplikace do popisků uchovávaných v reduxu.
 * @param descriptions zkonvertuje se na pole {@link IReduxEnumDescription}
 */
export const convertToReduxDescriptions = (descriptions: EnumDescription[]): ReadonlyArray<IReduxEnumDescription> => {
    return descriptions.map((des) => {
        return {
            intValue: des.IntValue,
            name: des.Name,
            locKey: des.LocKey,
            description: des.Description,
        };
    });
};

/**
 * Zjistí, zda je v pole enumů obsažen konkrétní enum pro zadaný klíč. Pokud pole klíč obsahuje, vrací `true`, v opačném případě `false`.
 * @param key klíč k identifikaci konkrétní položky enumu
 * @param enumType typ enumu, který se bude pro zadaný klíč prohledávat
 */
export const enumHas = (key: string | number, enumType: IReduxEnumType): boolean => enumType.descriptions.some(des => des.name === key || des.intValue === key);

/**
 * Pro zadaný klíč vrací konkrétní enum z pole enumů. Pokud pole enum neobsahuje, vrací `undefined`.
 * @param key klíč k identifikaci konkrétní položky enumu
 * @param enumType typ enumu, který se bude pro zadaný klíč prohledávat
 */
export const getFromEnum = (key: string | number, enumType: IReduxEnumType): IReduxEnumDescription | undefined => enumType.descriptions.find(des => des.name === key || des.intValue === key);

/**
 * Pro zadané pole enumů vrací názvy popisků, které slouží jako klíče.
 * @param enumType typ enumu, který se bude prohledávat
 */
export const getEnumKeys = (enumType: IReduxEnumType): string[] => enumType.descriptions.map(des => des.name);

/**
 * Zjistí, zda se jedná o akci s požadavkem na načtení enumu a zda režim batchování odpovídá zadanému parametru.
 * Umožňuje tak rozlišit akce se zapnutým/vypnutým batchováním.
 * @param batchingEnabled Pokud je true, odchytávají se akce se zapnutým batchováním. Pokud je false, odchytávají se akce s vypnutým batchováním.
 */
export const createLoadEnumActionPattern = (batchingEnabled: boolean) => (action: Action) => (
    action instanceof ILoadEnumsAction && action.payload.enableBatching === batchingEnabled
);

/**
 * Spojí enumy ze zadaných akcí. Výsledné pole obsahuje pouze unikátní výskyty, duplicity jsou odstraněny.
 * @param actions Akce, z nichž se získají data.
 */
export const mergeLoadEnumsActions = (actions: readonly ILoadEnumsAction[]): ReadonlyArray<string> => {
    const mergedData = actions.reduce((result, action) => {
        return [...result, ...action.payload.enumsKeys];
    }, [] as ReadonlyArray<string>);

    return [...new Set(mergedData)];
};

/**
 * Extrahuje z reduxových typů enumů lokalizační klíče a spojí je do jednoho pole. Duplicitní klíče jsou zahozeny.
 * @param enumTypesWithKeys pole reduxových typů enumů
 */
export const extractLocKeys = (enumTypesWithKeys: ReadonlyArray<EnumTypeWithKey>): ReadonlyArray<string> => {
    const locKeys: string[] = [];
    for (const enumTypeWithKey of enumTypesWithKeys) {
        locKeys.push(...enumTypeWithKey.enumType.descriptions.map((desc) => desc.locKey));
    }
    return [...new Set(locKeys)];
};
